// PhotoQueueSheet (R114 — Stacy: "saved photos from past months… who knows
// where they'll end up"): the ONE place an inspector sees every inspection
// photo still on THIS phone, grouped by inspection, with an honest answer to
// "where will these go?" — and the only place one can be thrown away.
//
//   • one row per inspection: property, visit day, count, MB, 4 thumbnails
//     (object URLs of the local blobs, all revoked on close) and the status
//     from GET /inspections/frame-owners (lib/frameQueuePolicy.mjs groupQueue):
//     will attach · will attach as "added after completion" · kept for the
//     office · rejected.
//   • Upload (primary): that inspection's photos go first (and its rejected
//     ones get one more try).
//   • ⋯ Save to phone: navigator.share with ~20 files per tap — each share needs
//     its own tap, and the File[] is built synchronously inside the tap (no
//     await before share, or iOS drops the gesture). Falls back to <a download>.
//     A share sheet cannot report whether the photos were saved, so the row
//     only says what was handed to it.
//   • ⋯ Discard…: only after a confirm that names the count and says these
//     photos exist only on this phone.
// Mounted on document.body (the WoQuickView pattern) so a page draw() can
// never destroy it; z-index sits under the shared ⋯ popover (340) and the
// Lightbox (1200) so both open above it.
import { Formatters } from '../lib/formatters';
import { kebabHtml, bindKebabs, closeKebab } from './Kebab';
import { openLightbox } from './Lightbox';
import { snapshot, prioritize, kick, discard, isSyncing, storageWarning, loadQueue, type FrameItem } from '../../lib/captureQueue';
import { groupQueue, chunk, fmtBytes, fmtDay, inspectionKeyOf, type FrameOwner, type QueueGroup } from '../../../../lib/frameQueuePolicy.mjs';

const esc = (x: any): string => Formatters.escapeHtml(String(x == null ? '' : x));
const ROOT_ID = 'fq-sheet';
const SHARE_BATCH = 20;

let current: PhotoQueueSheet | null = null;
// owners survive a close/reopen within the session (a missing inspection stays missing)
const ownerCache: Record<string, FrameOwner> = {};

export function openPhotoQueueSheet(opts: { focus?: string | null; current?: string | null } = {}): void {
  if (current) current.close();
  current = new PhotoQueueSheet(opts);
  current.mount();
}
export function closePhotoQueueSheet(): void { if (current) current.close(); }
export function photoQueueSheetOpen(): boolean { return !!(current && current.root.isConnected); }

class PhotoQueueSheet {
  root: HTMLElement = document.createElement('div');
  private opts: { focus?: string | null; current?: string | null };
  private urls = new Map<string, string>();     // item key → object URL (revoked on close)
  private shareAt: Record<string, number> = {}; // group key → next batch index
  private shared: Record<string, number> = {};  // group key → files handed to the share sheet
  private ownersErr = false;
  private loadingOwners = false;
  private redrawT: number | null = null;
  private onQueue = () => {
    if (this.redrawT) return;
    const later = () => {
      this.redrawT = null;
      if (!this.root.isConnected) return;
      // never yank an open ⋯ menu or the photo viewer out from under a tap
      if (document.querySelector('[data-kebab-pop]') || document.querySelector('.sx-lb')) { this.redrawT = window.setTimeout(later, 600); return; }
      this.draw();
    };
    this.redrawT = window.setTimeout(later, 250);
  };
  private onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && !document.querySelector('[data-kebab-pop]') && !document.querySelector('.sx-lb')) { e.preventDefault(); this.close(); }
  };

  constructor(opts: { focus?: string | null; current?: string | null }) { this.opts = opts; }

  mount(): void {
    injectStyle();
    this.root.id = ROOT_ID;
    this.root.className = 'fq';
    document.body.appendChild(this.root);
    document.addEventListener('keydown', this.onKey);
    window.addEventListener('surge-frame-queue', this.onQueue);
    this.draw();
    void loadQueue().then(() => { this.draw(); void this.loadOwners(); });
  }

  close(): void {
    closeKebab();
    this.root.remove();
    document.removeEventListener('keydown', this.onKey);
    window.removeEventListener('surge-frame-queue', this.onQueue);
    if (this.redrawT) { clearTimeout(this.redrawT); this.redrawT = null; }
    for (const u of this.urls.values()) { try { URL.revokeObjectURL(u); } catch { /* already gone */ } }
    this.urls.clear();
    if (current === this) current = null;
  }

  private async loadOwners(): Promise<void> {
    const ids = [...new Set(snapshot().map(inspectionKeyOf).filter((x): x is string => !!x))].filter(id => !ownerCache[id]);
    if (!ids.length) return;
    this.loadingOwners = true; this.draw();
    let failed = false;
    for (const part of chunk(ids, 200)) {
      try {
        const r = await fetch(`/api/assetops/inspections/frame-owners?ids=${encodeURIComponent(part.join(','))}`);
        if (!r.ok) { failed = true; continue; }
        const j = await r.json().catch(() => null);
        for (const o of ((j && j.owners) || [])) if (o && o.id) ownerCache[String(o.id)] = o;
      } catch { failed = true; }
    }
    this.ownersErr = failed;
    this.loadingOwners = false;
    if (this.root.isConnected) this.draw();
  }

  private urlFor(it: FrameItem): string {
    let u = this.urls.get(it.key);
    if (!u) { try { u = URL.createObjectURL(it.blob); this.urls.set(it.key, u); } catch { u = ''; } }
    return u;
  }

  private itemsOf(g: QueueGroup, items: FrameItem[]): FrameItem[] {
    return items.filter(it => (inspectionKeyOf(it) == null ? '__none__' : inspectionKeyOf(it)) === g.key);
  }

  private draw(): void {
    closeKebab();
    const items = snapshot();
    const groups = groupQueue(items, ownerCache);
    // release thumbnails whose photo has left the phone
    const live = new Set(items.map(i => i.key));
    for (const [k, u] of this.urls) if (!live.has(k)) { try { URL.revokeObjectURL(u); } catch { /* */ } this.urls.delete(k); }
    if (this.opts.focus) {
      const i = groups.findIndex(g => g.key === this.opts.focus);
      if (i > 0) groups.unshift(...groups.splice(i, 1));
    }
    const total = items.length;
    const bytes = items.reduce((s, i) => s + (i.size || 0), 0);
    const syncing = isSyncing();
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const pill = (s: string) => {
      const map: Record<string, [string, string]> = {
        attach: ['ok', 'Will attach'], late: ['info', 'Added after completion'], held: ['warn', 'For the office'],
        unknown: ['warn', 'For the office'], rejected: ['bad', 'Rejected'], checking: ['', 'Checking…'],
      };
      const [cls, txt] = map[s] || ['', s];
      return `<span class="fq-pill ${cls}">${esc(txt)}</span>`;
    };
    const rows = groups.map(g => {
      const its = this.itemsOf(g, items);
      const thumbs = its.slice(0, 4).map((it, i) => {
        const isImg = !it.blob || !it.blob.type || /^image\//i.test(it.blob.type);
        const u = isImg ? this.urlFor(it) : '';
        return u ? `<img src="${esc(u)}" alt="" data-fq-view="${esc(g.key)}|${i}">` : '<span class="fq-noimg">🎞</span>';
      }).join('');
      const more = its.length > 4 ? `<span class="fq-more" data-fq-view="${esc(g.key)}|4">+${its.length - 4}</span>` : '';
      const rejected = g.status === 'rejected';
      const batches = Math.ceil(its.length / SHARE_BATCH);
      const at = Math.min(this.shareAt[g.key] || 0, Math.max(0, batches - 1));
      const saveLabel = batches <= 1 ? `Save ${its.length} to phone` : `Save to phone — ${at * SHARE_BATCH + 1}–${Math.min(its.length, (at + 1) * SHARE_BATCH)} of ${its.length}`;
      const note = its.find(x => x.state === 'attention' && x.note)?.note;
      const primary = rejected
        ? `<button type="button" class="btn btn-primary btn-sm fq-go" data-fq-save="${esc(g.key)}">${esc(saveLabel)}</button>`
        : `<button type="button" class="btn btn-primary btn-sm fq-go" data-fq-up="${esc(g.key)}" ${offline ? 'disabled title="Offline — uploads resume when you reconnect"' : ''}>Upload</button>`;
      const menu = kebabHtml([
        ...(rejected ? [{ label: 'Try uploading again', icon: '↻', attrs: { 'data-fq-up': g.key } }] : [{ label: saveLabel, icon: '⤓', attrs: { 'data-fq-save': g.key } }]),
        { label: 'View all photos', icon: '🖼', attrs: { 'data-fq-view': `${g.key}|0` } },
        { divider: true },
        { label: `Discard ${g.count} photo${g.count === 1 ? '' : 's'}…`, icon: '🗑', danger: true, attrs: { 'data-fq-discard': g.key } },
      ], { tip: 'More', title: g.title });
      return `
        <div class="fq-row">
          <div class="fq-row-top">
            <div class="fq-row-main">
              <div class="fq-ttl">${esc(g.title)}</div>
              <div class="fq-sub">${this.opts.current && g.key === this.opts.current ? '<b class="fq-this">This inspection</b> · ' : ''}${g.day ? `Visit ${esc(fmtDay(g.day))} · ` : ''}${g.count} photo${g.count === 1 ? '' : 's'} · ${esc(fmtBytes(g.bytes))}${g.legacy ? ` · ${g.legacy} from the old app` : ''}</div>
            </div>
            ${pill(g.status)}
          </div>
          <div class="fq-thumbs">${thumbs}${more}</div>
          <div class="fq-status ${rejected ? 'bad' : ''}">${esc(g.label)}${note && (rejected || g.attention) ? `<span class="fq-why"> — ${esc(note === 'unreadable' ? 'this phone can no longer read the file' : note)}</span>` : ''}</div>
          ${this.shared[g.key] ? `<div class="fq-saved">Handed ${this.shared[g.key]} of ${its.length} to the share sheet — check your Photos app.</div>` : ''}
          <div class="fq-act">${primary}${menu}</div>
        </div>`;
    }).join('');
    this.root.innerHTML = `
      <div class="fq-backdrop" data-fq-close></div>
      <div class="fq-card" role="dialog" aria-modal="true" aria-label="Photos on this phone">
        <div class="fq-head">
          <div class="fq-head-main">
            <div class="fq-title">Photos on this phone</div>
            <div class="fq-head-sub">${total ? `${total} photo${total === 1 ? '' : 's'} · ${esc(fmtBytes(bytes))} — each stays here until the server confirms it has it.` : 'Nothing waiting — every photo from this phone has uploaded.'}</div>
          </div>
          <button type="button" class="fq-x" data-fq-close aria-label="Close">✕</button>
        </div>
        <div class="fq-body">
          ${storageWarning() ? '<div class="fq-warn">⚠ This browser would not store some photos offline — keep this screen open until they finish uploading.</div>' : ''}
          ${offline ? '<div class="fq-warn">You’re offline — uploads resume automatically when you reconnect.</div>' : ''}
          ${this.ownersErr ? '<div class="fq-warn">Couldn’t check where some of these go — they still upload; the server files each one.</div>' : ''}
          ${total ? `<div class="fq-all">
              <button type="button" class="btn btn-primary fq-allbtn" data-fq-upall ${syncing || offline ? 'disabled' : ''}>${syncing ? 'Uploading…' : `Upload all ${total}`}</button>
              <span class="fq-fine">${this.loadingOwners ? 'Checking where each inspection stands…' : 'Photos for an inspection that no longer exists are kept for the office to file — nothing is thrown away.'}</span>
            </div>` : ''}
          ${rows}
        </div>
      </div>`;
    this.bind(items, groups);
  }

  private bind(items: FrameItem[], groups: QueueGroup[]): void {
    const root = this.root;
    const gOf = (k: string) => groups.find(g => g.key === k);
    root.querySelectorAll<HTMLElement>('[data-fq-close]').forEach(el => el.addEventListener('click', () => this.close()));
    root.querySelector('[data-fq-upall]')?.addEventListener('click', () => { kick({ clear: true, priority: null }); this.draw(); });
    bindKebabs(root);
    // menu items get their own listeners (the ⋯ popover moves them out of root)
    root.querySelectorAll<HTMLElement>('[data-fq-up]').forEach(el => el.addEventListener('click', () => {
      const k = el.dataset.fqUp!;
      prioritize(k === '__none__' ? null : k, true);
      this.draw();
    }));
    root.querySelectorAll<HTMLElement>('[data-fq-view]').forEach(el => el.addEventListener('click', () => {
      const [k, i] = String(el.dataset.fqView).split('|');
      const g = gOf(k); if (!g) return;
      const its = this.itemsOf(g, items).filter(it => !it.blob || !it.blob.type || /^image\//i.test(it.blob.type));
      const us = its.map(it => this.urlFor(it)).filter(Boolean);
      if (us.length) openLightbox(us, Math.min(Number(i) || 0, us.length - 1), { caption: (n) => `${g.title} — ${n + 1} of ${us.length}` });
    }));
    root.querySelectorAll<HTMLElement>('[data-fq-save]').forEach(el => el.addEventListener('click', () => {
      const k = el.dataset.fqSave!;
      const g = gOf(k); if (!g) return;
      this.saveBatch(g, this.itemsOf(g, items));
    }));
    root.querySelectorAll<HTMLElement>('[data-fq-discard]').forEach(el => el.addEventListener('click', () => {
      const k = el.dataset.fqDiscard!;
      const g = gOf(k); if (!g) return;
      const its = this.itemsOf(g, items);
      const savedNote = this.shared[k] ? '' : '\n\nYou haven’t saved these to the phone yet — use “Save to phone” first if you might need them.';
      if (!window.confirm(`Discard ${its.length} photo${its.length === 1 ? '' : 's'} from ${g.title}?\n\nThese photos exist only on this phone. Once discarded they are gone for good — they will never upload.${savedNote}`)) return;
      void discard(its.map(i => i.key)).then(() => this.draw());
    }));
  }

  // Runs SYNCHRONOUSLY inside the tap: the File[] is built from blobs already
  // in memory and navigator.share is called before any await.
  private saveBatch(g: QueueGroup, its: FrameItem[]): void {
    const batches = chunk(its, SHARE_BATCH);
    if (!batches.length) return;
    const at = Math.min(this.shareAt[g.key] || 0, batches.length - 1);
    const batch = batches[at];
    const files = batch.map((it, i) => {
      const type = (it.blob && it.blob.type) || 'image/jpeg';
      const ext = /png/i.test(type) ? 'png' : /webm/i.test(type) ? 'webm' : /mp4/i.test(type) ? 'mp4' : 'jpg';
      const base = String(it.name || '').replace(/\.[^.]+$/, '') || 'photo';
      return new File([it.blob], `${base}-${at * SHARE_BATCH + i + 1}.${ext}`, { type, lastModified: it.queuedAt || Date.now() });
    });
    const advance = () => {
      this.shared[g.key] = Math.min(its.length, (this.shared[g.key] || 0) + files.length);
      this.shareAt[g.key] = (at + 1) % batches.length;
      if (this.root.isConnected) this.draw();
    };
    const nav: any = navigator;
    let canShare = false;
    try { canShare = !!(nav.share && nav.canShare && nav.canShare({ files })); } catch { canShare = false; }
    if (canShare) {
      nav.share({ files, title: `${g.title} — inspection photos` }).then(advance).catch((e: any) => {
        if (e && e.name === 'AbortError') return;   // they closed the share sheet
        this.download(files); advance();
      });
      return;
    }
    this.download(files);
    advance();
  }

  private download(files: File[]): void {
    files.forEach((f, i) => {
      window.setTimeout(() => {
        const u = URL.createObjectURL(f);
        const a = document.createElement('a');
        a.href = u; a.download = f.name; a.rel = 'noopener';
        document.body.appendChild(a); a.click(); a.remove();
        window.setTimeout(() => URL.revokeObjectURL(u), 4000);
      }, i * 350);
    });
  }
}

function injectStyle(): void {
  if (document.getElementById('fq-style')) return;
  const s = document.createElement('style');
  s.id = 'fq-style';
  s.textContent = `
    .fq { position:fixed; inset:0; z-index:330; display:flex; align-items:flex-end; justify-content:center; }
    @media (min-width:640px) { .fq { align-items:center; padding:16px; } }
    .fq-backdrop { position:absolute; inset:0; background:rgba(15,23,42,.5); }
    .fq-card { position:relative; background:var(--card); border:1px solid var(--border); border-radius:var(--radius-lg) var(--radius-lg) 0 0; box-shadow:var(--shadow-pop); width:100%; max-width:600px; max-height:92vh; overflow:auto; box-sizing:border-box; overscroll-behavior:contain; }
    @media (min-width:640px) { .fq-card { border-radius:var(--radius-lg); } }
    .fq-head { display:flex; gap:10px; align-items:flex-start; padding:14px 16px 10px; border-bottom:1px solid var(--border-soft); position:sticky; top:0; background:var(--card); z-index:1; }
    .fq-head-main { flex:1; min-width:0; }
    .fq-title { font-size:15px; font-weight:700; color:var(--text); letter-spacing:-0.01em; }
    .fq-head-sub { font-size:12px; color:var(--text-muted); margin-top:3px; line-height:1.45; }
    .fq-x { flex:none; width:36px; height:36px; border-radius:var(--radius-pill); border:none; background:transparent; color:var(--text-muted); font-size:15px; cursor:pointer; }
    .fq-x:hover { background:var(--bg-subtle); color:var(--text); }
    .fq-body { padding:12px 16px 18px; display:flex; flex-direction:column; gap:10px; }
    .fq-warn { padding:8px 10px; border-radius:var(--radius-sm); background:var(--warning-soft); color:var(--warning-text); font-size:12px; line-height:1.45; }
    .fq-all { display:flex; flex-direction:column; gap:6px; }
    .fq-allbtn { width:100%; justify-content:center; min-height:44px; }
    .fq-fine { font-size:11.5px; color:var(--text-faint); line-height:1.45; }
    .fq-row { border:1px solid var(--border); border-radius:var(--radius); background:var(--card); box-shadow:var(--shadow-card); padding:12px 14px; display:flex; flex-direction:column; gap:8px; }
    .fq-row-top { display:flex; gap:10px; align-items:flex-start; }
    .fq-row-main { flex:1; min-width:0; }
    .fq-ttl { font-size:14px; font-weight:700; color:var(--text); line-height:1.3; overflow-wrap:anywhere; }
    .fq-sub { font-size:12px; color:var(--text-muted); margin-top:2px; }
    .fq-this { color:var(--accent); font-weight:700; }
    .fq-pill { flex:none; display:inline-flex; align-items:center; font-size:10.5px; font-weight:700; padding:2px 9px; border-radius:var(--radius-pill); white-space:nowrap; background:var(--bg-subtle); color:var(--text-muted); line-height:1.5; }
    .fq-pill.ok { background:var(--success-soft); color:var(--success-text); }
    .fq-pill.info { background:var(--accent-soft); color:var(--accent); }
    .fq-pill.warn { background:var(--warning-soft); color:var(--warning-text); }
    .fq-pill.bad { background:var(--danger-soft); color:var(--danger-text); }
    .fq-thumbs { display:flex; gap:6px; align-items:center; }
    .fq-thumbs img, .fq-noimg { width:56px; height:56px; object-fit:cover; border-radius:var(--radius-sm); border:1px solid var(--border); display:flex; align-items:center; justify-content:center; cursor:pointer; background:var(--bg-subtle); flex:none; }
    .fq-more { font-size:12px; font-weight:700; color:var(--accent); cursor:pointer; padding:0 6px; min-height:36px; display:inline-flex; align-items:center; }
    .fq-status { font-size:12.5px; color:var(--text); line-height:1.45; }
    .fq-status.bad { color:var(--danger-text); }
    .fq-why { color:var(--text-muted); }
    .fq-saved { font-size:11.5px; color:var(--success-text); }
    .fq-act { display:flex; gap:8px; align-items:center; justify-content:flex-end; flex-wrap:wrap; }
    .fq-act .btn { min-height:36px; }
    .fq-act .kb-btn { min-width:36px; height:36px; }
  `;
  document.head.appendChild(s);
}
